import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { ARCADE_ACCENT, formatStatValue } from "../../utils/triviaArcade";
import {
  TRIVIA_CONTINUE_KEYS,
  TRIVIA_FEEDBACK_SAFE_PADDING_BOTTOM,
  canContinueTriviaFeedback,
} from "../../utils/triviaFeedbackContinue";
import {
  TRIVIA_FEEDBACK_CHROME_FLEX,
  TRIVIA_FEEDBACK_FOOTER_FLEX,
  TRIVIA_FEEDBACK_VERDICT_FLEX,
} from "../../utils/triviaFeedbackLayout";

const TriviaStatReveal = ({ sourceData }) => {
  const actual = sourceData?.players?.[0];
  if (sourceData?.threshold == null || actual?.statValue == null) return null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 360,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        bgcolor: "rgba(15, 23, 42, 0.7)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Actual
      </Typography>
      <Typography variant="h6" color="primary.main" fontWeight={700}>
        {formatStatValue(sourceData.subcategory, actual.statValue)}
      </Typography>
    </Box>
  );
};

const TriviaFeedbackVerdict = ({ lastResult, reducedMotion }) => (
  <Box
    data-testid="trivia-feedback-verdict"
    sx={{
      flex: TRIVIA_FEEDBACK_VERDICT_FLEX,
      minHeight: 0,
      overflow: "visible",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.25,
      px: { xs: 2, sm: 3 },
      pt: 1.5,
      pb: 1.25,
      textAlign: "center",
    }}
  >
    {lastResult ? (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.25,
          }}
        >
          {!reducedMotion && (
            <Typography
              variant="h5"
              fontWeight={800}
              color={lastResult.isCorrect ? "success.main" : "error.main"}
              lineHeight={1.15}
            >
              {lastResult.isCorrect ? `+${lastResult.points}` : "Wrong"}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" lineHeight={1.2}>
            {lastResult.isCorrect ? "Correct!" : "Not quite"}
          </Typography>
        </Box>
        <TriviaStatReveal sourceData={lastResult.sourceData} />
      </>
    ) : null}
  </Box>
);

const TriviaContinueFooter = ({ canContinue, onSkipFeedback }) => (
  <Box
    data-testid="trivia-continue-footer"
    data-pinned="true"
    data-safe-area="bottom"
    sx={{
      flex: TRIVIA_FEEDBACK_FOOTER_FLEX,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      px: { xs: 2, sm: 3 },
      pt: 1.25,
      pb: TRIVIA_FEEDBACK_SAFE_PADDING_BOTTOM,
      borderTop: "1px solid rgba(255,255,255,0.08)",
      bgcolor: "background.default",
    }}
  >
    <Button
      variant="contained"
      size="small"
      disabled={!canContinue}
      onClick={canContinue ? onSkipFeedback : undefined}
      aria-keyshortcuts={canContinue ? TRIVIA_CONTINUE_KEYS.join(" ") : undefined}
      sx={{
        fontWeight: 700,
        cursor: canContinue ? "pointer" : "default",
        ...(canContinue
          ? {
              bgcolor: ARCADE_ACCENT,
              "&:hover": { bgcolor: "#db2777" },
            }
          : {
              bgcolor: "action.disabledBackground",
              color: "text.disabled",
            }),
      }}
    >
      Tap to continue
    </Button>
  </Box>
);

const TriviaFeedbackOverlay = ({
  phase,
  lastResult,
  reducedMotion,
  dialogOpen = false,
  onSkipFeedback,
}) => {
  const canContinue = canContinueTriviaFeedback({
    phase,
    onSkipFeedback,
    dialogOpen,
  });

  return (
    <Box
      data-testid="trivia-feedback-chrome"
      data-fit="in-frame"
      sx={{
        flex: TRIVIA_FEEDBACK_CHROME_FLEX,
        minHeight: 0,
        overflow: "visible",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TriviaFeedbackVerdict
        lastResult={phase === "feedback" ? lastResult : null}
        reducedMotion={reducedMotion}
      />
      <TriviaContinueFooter
        canContinue={canContinue}
        onSkipFeedback={onSkipFeedback}
      />
    </Box>
  );
};

export default TriviaFeedbackOverlay;
